'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Volume2 } from 'lucide-react'
import type { LandingContent } from '@/types/landing'
import { track } from '@/lib/analytics'

type Props = { content: LandingContent['customerStories'] }
type SelectionSource = 'manual' | 'autoplay'
type MuxPlayerMethod = 'setCurrentTime' | 'unmute' | 'play'

const AUTOPLAY_DELAY_MS = 1_000

const muxPlayerCommand = (method: MuxPlayerMethod, value?: number) => JSON.stringify({
  context: 'player.js',
  version: '0.0.11',
  method,
  ...(value === undefined ? {} : { value }),
})

export default function CustomerStories({ content }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [eligible, setEligible] = useState(false)
  const [showUnmute, setShowUnmute] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  const rootRef = useRef<HTMLElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const inViewportRef = useRef(false)
  const autoplayTimerRef = useRef<number | null>(null)
  const active = content.stories[activeIndex]
  const nextIndex = (activeIndex + 1) % content.stories.length
  const next = content.stories[nextIndex]
  const playerSrc = useMemo(() => {
    try {
      const url = new URL(active.src)
      url.searchParams.set('autoplay', 'muted')
      url.searchParams.set('muted', 'true')
      url.searchParams.set('default-hidden-captions', 'false')
      url.searchParams.set('max-resolution', '2160p')
      url.searchParams.set('rendition-order', 'desc')
      url.searchParams.set('cap-rendition-to-player-size', 'false')
      url.searchParams.set('video-title', active.title)
      url.searchParams.set('accent-color', '#b80000')
      url.searchParams.set('primary-color', '#ffffff')
      return url.toString()
    } catch {
      return active.src
    }
  }, [active.src, active.title])

  const selectStory = useCallback((index: number, source: SelectionSource) => {
    setActiveIndex(index)
    track('customer_story_selected', {
      story_id: content.stories[index].id,
      selection_source: source === 'manual' ? 'manual' : 'automatic',
    })
  }, [content.stories])

  const clearAutoplayTimer = useCallback(() => {
    if (autoplayTimerRef.current === null) return
    window.clearTimeout(autoplayTimerRef.current)
    autoplayTimerRef.current = null
  }, [])

  const scheduleAutoplay = useCallback(() => {
    clearAutoplayTimer()
    autoplayTimerRef.current = window.setTimeout(() => {
      autoplayTimerRef.current = null
      if (inViewportRef.current && document.visibilityState !== 'hidden') setEligible(true)
    }, AUTOPLAY_DELAY_MS)
  }, [clearAutoplayTimer])

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const syncPreference = () => {
      setReducedMotion(query.matches)
      if (query.matches) setEligible(false)
    }

    syncPreference()
    query.addEventListener('change', syncPreference)
    return () => query.removeEventListener('change', syncPreference)
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!root || reducedMotion) return
    if (!('IntersectionObserver' in window)) {
      inViewportRef.current = true
      scheduleAutoplay()
      return clearAutoplayTimer
    }

    const observer = new IntersectionObserver(([entry]) => {
      inViewportRef.current = entry.isIntersecting
      if (entry.isIntersecting && document.visibilityState !== 'hidden') {
        scheduleAutoplay()
      } else {
        clearAutoplayTimer()
      }
    }, { threshold: 0.15 })

    observer.observe(root)
    return () => {
      observer.disconnect()
      clearAutoplayTimer()
    }
  }, [clearAutoplayTimer, reducedMotion, scheduleAutoplay])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearAutoplayTimer()
        setEligible(false)
      } else if (inViewportRef.current && !reducedMotion) {
        scheduleAutoplay()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [clearAutoplayTimer, reducedMotion, scheduleAutoplay])

  useEffect(() => {
    setShowUnmute(false)
  }, [activeIndex, eligible])

  const handleUnmute = () => {
    const playerWindow = iframeRef.current?.contentWindow
    if (!playerWindow) return

    playerWindow.postMessage(muxPlayerCommand('setCurrentTime', 0), 'https://player.mux.com')
    playerWindow.postMessage(muxPlayerCommand('unmute'), 'https://player.mux.com')
    playerWindow.postMessage(muxPlayerCommand('play'), 'https://player.mux.com')
    setShowUnmute(false)
  }

  const nextLabel = nextIndex === 1 ? 'second' : 'first'

  return <section
    id="customer-stories"
    className="fp-customer-stories"
    aria-label={content.label}
    ref={rootRef}
  >
    <div className="fp-customer-story-row" data-active-story={active.id}>
      <article className="fp-customer-story-active">
        {active.src && eligible ? <iframe
          key={active.id}
          ref={iframeRef}
          src={playerSrc}
          title={active.title}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => setShowUnmute(true)}
        /> : active.poster ? <div
          className="fp-customer-story-poster fp-customer-story-poster--image"
          style={{ backgroundImage: `url(${active.poster})` }}
          role="img"
          aria-label={`${active.title} video thumbnail`}
        /> : <div className="fp-customer-story-poster" aria-hidden="true"><span>FP</span></div>}
        {showUnmute && <button
          type="button"
          className="fp-film-unmute"
          onClick={handleUnmute}
          aria-label={`Unmute ${active.title} film`}
        >
          <Volume2 aria-hidden="true" />
          <span>Unmute</span>
        </button>}
      </article>
      <button
        className="fp-customer-story-next"
        onClick={() => selectStory(nextIndex, 'manual')}
        aria-label={`Show ${nextLabel} customer film`}
      >
        <span
          className="fp-customer-story-next-art"
          style={next.poster ? { backgroundImage: `url(${next.poster})` } : undefined}
          aria-hidden="true"
        />
        <small>Next up</small>
        <strong>{next.title}</strong>
        <span>{next.status}</span>
      </button>
    </div>
  </section>
}
